import React, { useEffect, useState } from 'react';
import { FaRocket, FaBolt, FaGlobe } from 'react-icons/fa';
import useCaseSaas from '../assets/images/use_case_1.png';
import useCaseMigration from '../assets/images/use_case_2.png';
import useCaseMesh from '../assets/images/use_case_3.png';

// Match ServicesOverview color palette exactly
const palette = {
  highlight: "#f6f6ff", // animations
  button: "#f6f6ff",   // buttons
  base: "#f8f8f8",     // base
  text: "#0c0c16",     // main text
  cardBg: "#fffff",
  cardShadow: "rgba(64,46,122,0.08)",
  subtitle: "#702fff",  // subtitle highlight
  icon1: '#3dc2ec',
  icon2: '#4b70f5',
  icon3: '#702fff',
};

const useCases = [
  {
    icon: <FaRocket size={32} color={palette.icon1} />,
    title: '360° Business Insights',
    problem: 'Fragmented SaaS data makes it hard to get a unified view of customers, marketing, or sales.',
    solution: 'We unify all your SaaS data into a Governed Data Lakehouse for 360° dashboards and AI-powered insights and actions.',
    example: 'E-commerce client unified data from Salesforce, Klaviyo, Shopify, Zendesk —boosted retention by 15% in 3 months.',
    color: palette.icon1,
    img: useCaseSaas,
    imgAlt: 'Unified SaaS Data',
  },
  {
    icon: <FaBolt size={32} color={palette.icon2} />,
    title: 'Fast-Track Cloud Migration',
    problem: 'On-premise data warehouses are costly and slow. Cloud migration is complex and takes time.',
    solution: 'Our platform accelerates migration to a secure, scalable Lakehouse with built-in governance and analytics.',
    example: 'Logistics company migrated from an on-premise Oracle Server Warehouse to the Unified Data Lakehouse in 3 weeks —5 teams live, no extra infra.',
    color: palette.icon2,
    img: useCaseMigration,
    imgAlt: 'Migration of Platform',
  },
  {
    icon: <FaGlobe size={32} color={palette.icon3} />,
    title: 'Enterprise-Grade AI Platform',
    problem: 'Scaling teams need a robust platform for AI, Governance, and self-service at Scale.',
    solution: 'Automated ingestion, scalable Data Platform, and built-in tools for Data Scientists and Engineers —No silos.',
      example: 'Financial institution built a cross-domain platform — data teams deployed data products in hours instead of weeks, ensured compliance.',
    color: palette.icon3,
    img: useCaseMesh,
    imgAlt: 'Scalable Lakehouse Mesh',
  },
];

const UseCasesSection = () => {
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [cardVisibles, setCardVisibles] = useState(Array(useCases.length).fill(false));

  // Animation triggers on mount (no scroll required)
  useEffect(() => {
    let subtitleTimeout, titleTimeout, descTimeout, cardsTimeout;
    subtitleTimeout = setTimeout(() => setShowSubtitle(true), 200);
    titleTimeout = setTimeout(() => setShowTitle(true), 600);
    descTimeout = setTimeout(() => setShowDesc(true), 800);
    cardsTimeout = setTimeout(() => setShowCards(true), 800);
    return () => {
      clearTimeout(subtitleTimeout);
      clearTimeout(titleTimeout);
      clearTimeout(descTimeout);
      clearTimeout(cardsTimeout);
    };
  }, []);

  useEffect(() => {
    if (showCards) {
      useCases.forEach((_, idx) => {
        setTimeout(() => {
          setCardVisibles(prev => {
            const updated = [...prev];
            updated[idx] = true;
            return updated;
          });
        }, idx * 180);
      });
    } else {
      setCardVisibles(Array(useCases.length).fill(false));
    }
  }, [showCards]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <section style={{
      padding: isMobile ? '30px 15px' : '60px 20px',
      background: palette.base,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '1400px',
      margin: '0 auto',
      marginTop: isMobile ? '80px' : '120px',
      marginBottom: isMobile ? '80px' : '120px', // Add bottom margin for spacing between components
      textAlign: 'center',
    }}>
      {/* Main Header */}
      <div style={{
        maxWidth: '900px', // Increased from 700px to match ServicesOverview
        textAlign: 'center',
        marginBottom: isMobile ? '35px' : '50px',
        width: '100%',
      }}>
        <span
          style={{
            color: palette.subtitle,
            fontWeight: '700',
            fontSize: isMobile ? '13px' : '18px', // Match ServicesOverview mobile fontSize
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            display: 'block',
            fontFamily: 'Inter, sans-serif',
            marginBottom: isMobile ? '8px' : '12px', // Match ServicesOverview mobile margin
            opacity: showSubtitle ? 1 : 0,
            transform: showSubtitle ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out'
          }}
        >Use Cases</span>
        <h2
          style={{
            fontSize: isMobile ? '24px' : '36px', // Changed from 32px back to 24px to match ServicesOverview mobile sizing
            fontWeight: '400',
            color: palette.text,
            margin: isMobile ? '0 0 10px 0' : '0 0 18px 0',
            lineHeight: '1.15',
            fontFamily: 'Inter, sans-serif',
            opacity: showTitle ? 1 : 0,
            transform: showTitle ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out 0.2s',
            textAlign: 'center'
          }}
        >See how our platform drives impact</h2>
        <p
          style={{
            color: '#666',
            fontSize: isMobile ? '13px' : '18px', // Keep ServicesOverview sizing
            fontWeight: 300,
            margin: isMobile ? '0 0 24px 0' : '0 auto 0 auto', // Match ServicesOverview mobile margin
            marginBottom: isMobile ? '24px' : '30px',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.6',
            maxWidth: isMobile ? '400px' : '800px', // Match ServicesOverview mobile maxWidth
            textAlign: 'center',
            opacity: showDesc ? 1 : 0,
            transform: showDesc ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out 0.4s'
          }}
        >
          At Dataqly, we help businesses turn data challenges into competitive advantages. From scattered systems to complex migrations, discover how our proven solutions deliver unified insights, fast migration, and AI at scale.
        </p>
      </div>

      {/* Use Cases - Vertical Layout */}
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '40px' : '70px', // Reduced mobile gap to match better spacing
        width: '100%',
        maxWidth: isMobile ? '400px' : '1100px', // Match ServicesOverview mobile maxWidth
      }}>
        {useCases.map((uc, idx) => (
          <React.Fragment key={uc.title}>
            <div
              style={{
                opacity: cardVisibles[idx] ? 1 : 0,
                transform: cardVisibles[idx] ? 'translateY(0)' : 'translateY(40px)',
                transition: 'all 0.8s ease-out',
                background: 'transparent',
              }}
            >
              {/* Image Section - Full Width on Top */}
              <div style={{
                width: '100%',
                position: 'relative',
                marginBottom: isMobile ? '20px' : '30px', // Reduced margin
              }}>
                <div style={{
                  position: 'relative',
                  borderRadius: '12px', // Slightly smaller radius
                  overflow: 'hidden',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.1)', // Reduced shadow
                  transition: 'transform 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}>
                  <img
                    src={uc.img}
                    alt={uc.imgAlt}
                    style={{
                      width: '100%',
                      height: isMobile ? '220px' : '320px', // Reduced from 280px/400px
                      objectFit: 'cover',
                      objectPosition: idx === 0 ? 'center 30%' : 
                                    idx === 1 ? 'center 20%' : 
                                    'center 40%',
                      display: 'block',
                    }}
                  />

                  {/* Gradient overlay */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${uc.color}20, transparent 60%)`,
                  }} />
                </div>
                
                {/* Use Case Number Badge */}
                <div style={{
                  position: 'absolute',
                  bottom: '15px', // Reduced from 20px
                  left: '15px',
                  fontSize: isMobile ? '11px' : '13px', // Reduced font size
                  fontWeight: '600',
                  color: uc.color,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontFamily: 'Inter, sans-serif',
                  background: 'rgba(255,255,255,0.95)',
                  padding: isMobile ? '6px 12px' : '7px 14px', // Reduced padding
                  borderRadius: '10px', // Slightly smaller radius
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)', // Reduced shadow
                }}>
                  Use Case {idx + 1}
                </div>
              </div>

              {/* Content Section - Below Image */}
              <div style={{
                width: '100%',
                textAlign: isMobile ? 'center' : 'left',
                padding: isMobile ? '0 8px' : '0 15px', // Reduced padding
              }}>
                {/* Title */}
                <h3 style={{
                  fontSize: isMobile ? '16px' : '24px', // Match ServicesOverview card title mobile sizing
                  fontWeight: '700',
                  color: palette.text,
                  margin: isMobile ? '0 0 6px 0' : '0 0 10px 0', // Match ServicesOverview mobile margin
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: '1.3',
                }}>
                  {uc.title}
                </h3>

                {/* Main Description */}
                <p style={{
                  fontSize: isMobile ? '14px' : '16px', // Match ServicesOverview card description mobile sizing
                  color: '#666',
                  fontWeight: '300',
                  margin: '0 0 20px 0',
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: '1.5', // Match ServicesOverview lineHeight
                }}>
                  {uc.solution}
                </p>

                {/* Challenge & Success Story - Side by Side on Desktop */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '16px' : '25px', // Reduced gap
                }}>
                  {/* Challenge */}
                  <div style={{
                    padding: isMobile ? '20px 8px 16px 8px' : '20px', // Match ServicesOverview card mobile padding
                    background: `linear-gradient(135deg, ${uc.color}15, ${uc.color}08)`,
                    borderRadius: '12px',
                    borderLeft: `3px solid ${uc.color}`,
                    transition: 'all 0.3s ease',
                    boxShadow: `0 3px 12px ${palette.cardShadow}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(64,46,122,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 3px 12px ${palette.cardShadow}`;
                    }
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '10px', // Reduced margin
                    }}>
                      <div style={{
                        width: '24px', // Reduced from 28px
                        height: '24px',
                        background: `linear-gradient(135deg, #b8baff 0%, ${uc.color} 50%, #7bd7f7 100%)`,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px', // Reduced margin
                        boxShadow: "0 2px 6px rgba(64,46,122,0.10)", // Reduced shadow
                      }}>
                        <span style={{
                          color: 'white',
                          fontSize: '12px', // Reduced font size
                          fontWeight: 'bold',
                        }}>!</span>
                      </div>
                      <h4 style={{
                        fontSize: isMobile ? '11px' : '14px', // Smaller mobile font
                        fontWeight: '700',
                        color: uc.color,
                        margin: '0',
                        fontFamily: 'Inter, sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Challenge
                      </h4>
                    </div>
                    <p style={{
                      fontSize: isMobile ? '11px' : '14px', // Smaller mobile font
                      color: '#666',
                      fontWeight: '300',
                      margin: '0',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.4',
                      paddingLeft: isMobile ? '28px' : '34px', // Reduced mobile padding
                    }}>
                      {uc.problem}
                    </p>
                  </div>

                  {/* Success Story */}
                  <div style={{
                    padding: isMobile ? '20px 8px 16px 8px' : '20px', // Match ServicesOverview card mobile padding
                    background: `linear-gradient(135deg, #10b98120, #10b98110)`,
                    borderRadius: '12px',
                    borderLeft: `3px solid #10b981`,
                    transition: 'all 0.3s ease',
                    marginTop: isMobile ? '0' : '0',
                    boxShadow: `0 3px 12px ${palette.cardShadow}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(64,46,122,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 3px 12px ${palette.cardShadow}`;
                    }
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '10px', // Reduced margin
                    }}>
                      <div style={{
                        width: '24px', // Reduced from 28px
                        height: '24px',
                        background: 'linear-gradient(135deg, #b8baff 0%, #10b981 50%, #7bd7f7 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px', // Reduced margin
                        boxShadow: "0 2px 6px rgba(64,46,122,0.10)", // Reduced shadow
                      }}>
                        <span style={{
                          color: 'white',
                          fontSize: '12px', // Reduced font size
                          fontWeight: 'bold',
                        }}>✓</span>
                      </div>
                      <h4 style={{
                        fontSize: isMobile ? '11px' : '14px', // Smaller mobile font
                        fontWeight: '700',
                        color: '#10b981',
                        margin: '0',
                        fontFamily: 'Inter, sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Success Story
                      </h4>
                    </div>
                    <p style={{
                      fontSize: isMobile ? '11px' : '14px', // Smaller mobile font
                      color: '#666',
                      fontWeight: '300',
                      margin: '0',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.4',
                      paddingLeft: isMobile ? '28px' : '34px', // Reduced mobile padding
                    }}>
                      {uc.example}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Creative Divider - Only show between items, not after the last one */}
            {idx < useCases.length - 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: isMobile ? '30px 0' : '40px 0', // Reduced margin
                opacity: cardVisibles[idx] ? 1 : 0,
                transition: 'opacity 0.8s ease-out 0.5s',
              }}>
                {/* Left gradient line */}
                <div style={{
                  flex: 1,
                  height: '2px',
                  background: `linear-gradient(90deg, transparent, ${useCases[idx + 1].color}40, ${useCases[idx + 1].color}80)`,
                  marginRight: '15px', // Reduced margin
                }} />
                
                {/* Center icon with next use case color */}
                <div style={{
                  width: isMobile ? '35px' : '42px', // Reduced size
                  height: isMobile ? '35px' : '42px',
                  background: `linear-gradient(135deg, ${useCases[idx + 1].color}15, ${useCases[idx + 1].color}25)`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${useCases[idx + 1].color}40`,
                  position: 'relative',
                }}>
                  {/* Animated pulse effect */}
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: `${useCases[idx + 1].color}20`,
                    animation: 'pulse 2s infinite',
                  }} />
                  
                  {/* Next use case icon preview */}
                  {React.cloneElement(useCases[idx + 1].icon, { 
                    size: isMobile ? 14 : 18, // Reduced size
                    color: useCases[idx + 1].color 
                  })}
                </div>
                
                {/* Right gradient line */}
                <div style={{
                  flex: 1,
                  height: '2px',
                  background: `linear-gradient(90deg, ${useCases[idx + 1].color}80, ${useCases[idx + 1].color}40, transparent)`,
                  marginLeft: '15px', // Reduced margin
                }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Add CSS animation for pulse effect */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.08);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </section>
  );
};

export default UseCasesSection;
